import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  Tags,
  AlertCircle,
  Layers,
} from 'lucide-react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { filterItemsBySearch } from '../utils/textSanitize';

const COLLECTION = 'Categorias_Insumos';

const TIPOS_INSUMO = [
  { value: 'ingrediente', label: 'Ingrediente' },
  { value: 'embalagem', label: 'Embalagem' },
  { value: 'servico', label: 'Serviço' },
  { value: 'limpeza_conservacao', label: 'Limpeza e Conservação' },
];

const labelTipo = (v) => {
  const key = !v || v === 'materia_prima' ? 'ingrediente' : v;
  return TIPOS_INSUMO.find((t) => t.value === key)?.label || v;
};

/**
 * Normaliza subcategorias para lista alfabética.
 * Aceita array, string "A, B, C" ou mapa; separa vírgulas (legado) e remove duplicatas.
 */
const sortSubcategorias = (raw) => {
  const pieces = [];
  const pushChunk = (value) => {
    String(value ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => pieces.push(s));
  };

  if (Array.isArray(raw)) {
    raw.forEach(pushChunk);
  } else if (typeof raw === 'string') {
    pushChunk(raw);
  } else if (raw && typeof raw === 'object') {
    Object.values(raw).forEach(pushChunk);
  }

  return [...new Set(pieces)].sort((a, b) =>
    a.localeCompare(b, 'pt-BR', { sensitivity: 'base', numeric: true })
  );
};

const emptyForm = () => ({
  tipo_insumo: 'ingrediente',
  categoria: '',
  subcategorias: [],
});

const CategoriasInsumosPage = () => {
  const { isRole } = useAuth();
  const isSuperadmin = isRole('superadmin');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [novaSubcategoria, setNovaSubcategoria] = useState('');
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, COLLECTION), orderBy('categoria'));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          subcategorias: sortSubcategorias(data.subcategorias),
        };
      });
      setItems(rows);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (filterTipo) {
      list = list.filter((r) => {
        const t = !r.tipo_insumo || r.tipo_insumo === 'materia_prima' ? 'ingrediente' : r.tipo_insumo;
        return t === filterTipo;
      });
    }
    return filterItemsBySearch(list, searchTerm, [
      'categoria',
      (r) => (Array.isArray(r.subcategorias) ? r.subcategorias.join(' ') : ''),
    ]);
  }, [items, searchTerm, filterTipo]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setNovaSubcategoria('');
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      tipo_insumo: (!row.tipo_insumo || row.tipo_insumo === 'materia_prima'
        ? 'ingrediente'
        : row.tipo_insumo),
      categoria: row.categoria || '',
      subcategorias: sortSubcategorias(row.subcategorias),
    });
    setNovaSubcategoria('');
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!formData.tipo_insumo) {
      e.tipo_insumo = 'Selecione o tipo de insumo';
    }
    if (!formData.categoria || !String(formData.categoria).trim()) {
      e.categoria = 'Informe o nome da categoria';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = {
      tipo_insumo: formData.tipo_insumo,
      categoria: String(formData.categoria).trim(),
      subcategorias: sortSubcategorias(formData.subcategorias),
      updatedAt: serverTimestamp(),
    };

    try {
      setSaving(true);
      if (editingId) {
        await updateDoc(doc(db, COLLECTION, editingId), payload);
      } else {
        await addDoc(collection(db, COLLECTION), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      console.error(err);
      setErrors({ _submit: err.message || 'Não foi possível salvar.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const addSubcategoria = () => {
    const t = novaSubcategoria.trim();
    if (!t) return;
    if (formData.subcategorias.includes(t)) {
      setNovaSubcategoria('');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      subcategorias: sortSubcategorias([...prev.subcategorias, t]),
    }));
    setNovaSubcategoria('');
  };

  const removeSubcategoria = (idx) => {
    setFormData((prev) => ({
      ...prev,
      subcategorias: prev.subcategorias.filter((_, i) => i !== idx),
    }));
  };

  if (!isSuperadmin) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Acesso restrito ao superadministrador.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md">
              <Tags className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Categorias de Insumos</h1>
              <p className="text-sm text-gray-600">
                Parâmetros globais: tipo de insumo, categoria e subcategorias opcionais.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Nova categoria
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-3 rounded-xl bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Buscar por categoria ou subcategoria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-400" />
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            >
              <option value="">Todos os tipos</option>
              {TIPOS_INSUMO.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              Nenhum registro encontrado. Cadastre categorias para uso nos insumos.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Tipo</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Categoria</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Subcategorias</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/80">
                      <td className="whitespace-nowrap px-4 py-3 text-gray-800">
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800">
                          {labelTipo(row.tipo_insumo)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.categoria}</td>
                      <td className="max-w-md px-4 py-3 text-gray-600">
                        {(() => {
                          const subs = sortSubcategorias(row.subcategorias);
                          return subs.length > 0 ? subs.join(', ') : '—';
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="mr-2 inline-flex rounded p-1.5 text-indigo-600 hover:bg-indigo-50"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(row)}
                          className="inline-flex rounded p-1.5 text-red-600 hover:bg-red-50"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Editar categoria' : 'Nova categoria'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {errors._submit && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {errors._submit}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Tipo de insumo <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.tipo_insumo}
                  onChange={(e) => setFormData((p) => ({ ...p, tipo_insumo: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                >
                  {TIPOS_INSUMO.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {errors.tipo_insumo && (
                  <p className="mt-1 text-xs text-red-600">{errors.tipo_insumo}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Categoria <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.categoria}
                  onChange={(e) => setFormData((p) => ({ ...p, categoria: e.target.value }))}
                  placeholder="Ex.: Farinhas, Embalagens flexíveis..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
                {errors.categoria && (
                  <p className="mt-1 text-xs text-red-600">{errors.categoria}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Subcategorias (opcional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novaSubcategoria}
                    onChange={(e) => setNovaSubcategoria(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSubcategoria();
                      }
                    }}
                    placeholder="Digite e adicione"
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addSubcategoria}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50"
                  >
                    Adicionar
                  </button>
                </div>
                {formData.subcategorias.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {formData.subcategorias.map((s, idx) => (
                      <li
                        key={`${s}-${idx}`}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-800"
                      >
                        {s}
                        <button
                          type="button"
                          onClick={() => removeSubcategoria(idx)}
                          className="rounded p-0.5 hover:bg-gray-200"
                          aria-label={`Remover ${s}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Excluir categoria?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Remover <strong>{deleteConfirm.categoria}</strong> ({labelTipo(deleteConfirm.tipo_insumo)})?
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm.id)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriasInsumosPage;
